import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { privateApi } from '../../../api/api';
import { toast } from 'sonner';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import clsx from 'clsx';

interface FullSyncTabProps {
    onClose: () => void;
}
interface FullSyncForm {
    sync: boolean;
}

const autoSyncSchema = z.object({
    sync: z.boolean(),
});

const FullSyncTab: React.FC<FullSyncTabProps> = ({ onClose }) => {
    const localStorageData = JSON.parse(localStorage.getItem('user') || '{}');

    const { handleSubmit, register, formState: { errors }, reset } = useForm<FullSyncForm>({
        resolver: zodResolver(autoSyncSchema),
        defaultValues: {
            sync: false
        }
    });

    useEffect(() => {
        if (localStorageData?.is_full_sync !== undefined) {
            reset({
                sync: localStorageData.is_full_sync,
            });
        }
    }, [localStorageData]);

    const { mutate: autoSync } = useMutation({
        mutationFn: (data: FullSyncForm) => privateApi.post('/v3/setting/enable-full-sync', {
            is_full_sync: data.sync ? 0 : 1,
        }),
        onSuccess: async (data) => {
            reset({
                sync: data?.data?.data?.is_full_sync || false,
            })
            toast.success('Auto Sync has been updated', {
                id: 'auto-sync',
            });
        },
        onError: (error) => {
            toast.error((error as any)?.response?.data?.message || 'Failed to update auto sync', {
                id: 'auto-sync',
            });
        }
    })

    const onSubmit = useCallback((data: FullSyncForm) => {
        toast.loading('Updating Auto Sync...', {
            id: 'auto-sync',
        });
        autoSync(data);
    }, [autoSync]);

    const { mutate: manualSync, isPending } = useMutation({
        mutationFn: () => privateApi.get('/v3/setting/full-sync'),
        onSuccess: () => {
            toast.success('Manual Sync has been initiated', {
                id: 'manual-sync',
            });
        },
        onError: (error) => {
            toast.error((error as any)?.response?.data?.message || 'Failed to initiate manual sync', {
                id: 'manual-sync',
            });
        }
    });

    const onManualSync = useCallback(() => {
        toast.loading('Initiating Manual Sync...', {
            id: 'manual-sync',
        });
        manualSync();
    }, [manualSync]);

    useEffect(() => {
        return () => {
            reset();
        }
    }, [onClose]);

    return (
        <div className="d-flex flex-column" style={{ height: '75vh' }}>
            <div className="action-sheet-content flex-grow-1">
                <div className="row align-items-center">
                    <label className="col-sm-4 col-form-label d-flex justify-content-end">
                        Auto Sync:
                    </label>
                    <div className="col-sm-8">
                        <form id="UISyncForm" onSubmit={handleSubmit(onSubmit)}>
                            <div className="form-check form-switch mb-0">
                                <input
                                    className={clsx("form-check-input", errors.sync && 'is-invalid')}
                                    type="checkbox"
                                    id="flexSwitchCheckDefault"
                                    {...register('sync')}
                                    onChange={handleSubmit(onSubmit)}
                                />
                                {errors.sync && (
                                    <div className="invalid-feedback">{typeof errors.sync.message === 'string' && errors.sync.message}</div>
                                )}
                                <label className="form-check-label" htmlFor="flexSwitchCheckDefault">
                                    Enable auto sync
                                </label>
                            </div>
                        </form>
                    </div>
                </div>

                <div className="row mb-3 align-items-center">
                    <div className="col-sm-4 col-form-label d-flex justify-content-end" />
                    <div className="col-sm-8 alert alert-info">
                        <p className="text-muted mb-0">
                            Auto Sync will automatically sync data with the channel manager each day when enabled.
                        </p>
                    </div>
                </div>

                {/* Manual Sync Section */}
                <div className="row mb-2 align-items-center">
                    <label className="col-sm-4 col-form-label d-flex justify-content-end">Manual Sync:</label>
                    <div className="col-sm-8">

                        <button
                            className="btn btn-primary"
                            disabled={isPending}
                            onClick={onManualSync}
                        >
                            {isPending ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                    Syncing...
                                </>
                            ) : (
                                "Sync Now"
                            )}
                        </button>
                    </div>
                </div>

                <div className="row mb-3 align-items-center">
                    <div className="col-sm-4 col-form-label d-flex justify-content-end" />
                    <div className="col-sm-8 alert alert-warning">
                        <p className="text-muted mb-0">
                            If you want to perform a manual sync, click the button above.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default FullSyncTab